import React, { useEffect, useMemo, useState } from 'react';
import { chain } from 'lodash';
import { InfoBox, Marker } from '@react-google-maps/api';
import { StepComponent } from '../types';
import Yup from '../../../../utils/yup';
import useAsyncFetch from '../../../../hooks/use-async';
import {
  MapService,
  PropriedadesService,
  RecomendacaoSemeaduraMapaService,
} from '../../../../services';
import getMarker from '../../../../components/Map/custom-marker';
import Subtitle from '../../../../components/Map/subtitle';
import Polygon from '../../../../components/Map/polygon';
import Map from '../../../../components/Map';
import { getCultivaresAttributes } from '../../utils';
import { RecomendacaoSemeaduraCultivarToSave } from '../../../../services/recomendacoes-semeadura';

const getOrderedCultivar = (
  cultivares: RecomendacaoSemeaduraCultivarToSave[],
) =>
  chain(cultivares)
    .reduce<RecomendacaoSemeaduraCultivarToSave[]>((acc, cultivar) => {
      if (!acc.find((item) => item.glebaId === cultivar.glebaId)) {
        acc.push(cultivar);
      }

      return acc;
    }, [])
    .orderBy('dataInicio')
    .map((item, index) => ({ ...item, order: index + 1 }))
    .value();

const Mapa: StepComponent = ({ formObject }) => {
  const [visibleInfoId, setVisibleInfoId] = useState<number>();
  const [mousePosition, setMousePosition] = useState<google.maps.LatLng>();
  const [map, setMap] = useState<google.maps.Map>();

  const formValue = formObject.form.getValues();

  const cultivares = getCultivaresAttributes(formValue, true);

  const { data: propriedades } = useAsyncFetch(
    {
      callback: () => PropriedadesService.list(),
      errorMessage: 'Falha ao carregar propriedades.',
      withLoader: true,
    },
    [],
  );

  const talhoes = useMemo(
    () =>
      propriedades?.find(({ id }) => formValue.propriedade?.id === id)?.glebas,
    [formValue.propriedade, propriedades],
  );

  const mapaService = useMemo(() => {
    if (!cultivares) return;

    return new RecomendacaoSemeaduraMapaService(cultivares);
  }, [cultivares]);

  const talhoesCoordenadas = useMemo(() => {
    if (!mapaService || !cultivares) return [];

    return talhoes?.map((talhao) => {
      const { id, descricao, coordinates } = talhao;
      const paths = mapaService.getPolygonsCoordinates(coordinates);
      const center = MapService.getPolygonCenter(
        coordinates,
        `talhao ${id} - ${descricao}`,
      );

      const cultivaresOrdenadas = getOrderedCultivar(cultivares);

      const order = cultivaresOrdenadas.find(
        (item) => item.glebaId === talhao.id,
      )?.order;

      let color = '#FFF';
      if (mapaService.getCultivaresTalhao(id)) {
        color = mapaService.getCorTalhao(talhao.id);
      }

      const options = {
        strokeColor: color,
        fillColor: color,
      };

      return {
        id,
        order,
        descricao,
        paths,
        center,
        options,
      };
    });
  }, [mapaService, talhoes, cultivares]);

  const legendas = useMemo(() => {
    if (!mapaService || !talhoes) return [];

    return mapaService.getLegendas(talhoes);
  }, [mapaService, talhoes]);

  useEffect(() => {
    if (!map || !talhoes?.length) return;

    const coordenadas = talhoes.map(({ coordinates }) => coordinates);
    const center = MapService.getPolygonsCenter(coordenadas);

    if (center) {
      map?.moveCamera({ center, zoom: 13 });
    }
  }, [map, talhoes]);

  const onMouseMove = (event: google.maps.MapMouseEvent, id: number) => {
    const latLng = event.latLng || undefined;
    setMousePosition(latLng);
    setVisibleInfoId(id);
  };

  const onMouseOut = () => {
    setVisibleInfoId(undefined);
  };

  const onMapLoad = (mapLoaded: google.maps.Map) => {
    setMap(mapLoaded);
  };

  return (
    <div className="recomendacoes-semeadura-create-edit-resumo">
      <div className="flex flex-column recomendacao-semeadura-map">
        <Map onLoad={onMapLoad}>
          {talhoesCoordenadas &&
            talhoesCoordenadas.map(
              ({ id, descricao, order, paths, center, options }) => {
                const markerUri = getMarker({
                  fill: options.fillColor,
                  stroke: options.strokeColor,
                  heigth: 40,
                });

                return (
                  <React.Fragment key={id}>
                    <Polygon
                      paths={paths}
                      onMouseMove={(e: google.maps.MapMouseEvent) => {
                        onMouseMove(e, id);
                      }}
                      onMouseOut={onMouseOut}
                      options={options}
                    />
                    {order && center && (
                      <Marker
                        icon={markerUri}
                        position={center}
                        label={`${order}`}
                      />
                    )}
                    {visibleInfoId && mousePosition && (
                      <InfoBox
                        position={mousePosition}
                        options={{
                          visible: visibleInfoId === id,
                          closeBoxURL: '',
                        }}
                      >
                        <p className="info-box">{descricao}</p>
                      </InfoBox>
                    )}
                  </React.Fragment>
                );
              },
            )}
        </Map>
        {legendas?.length && <Subtitle legendas={legendas} />}
      </div>
    </div>
  );
};

Mapa.validationSchema = Yup.object();

export default Mapa;
